// Cover element definitions and metadata for professional book cover generation

export type CoverType = 'front' | 'back' | 'spine' | 'full_wrap';

export type ElementType =
  | 'title'
  | 'author'
  | 'subtitle'
  | 'series'
  | 'tagline'
  | 'endorsement'
  | 'award'
  | 'publisher_logo'
  | 'blurb'
  | 'bio'
  | 'author_photo'
  | 'barcode'
  | 'isbn'
  | 'price'
  | 'qr_code'
  | 'social_media'
  | 'publisher_info';

export type ElementPlacement =
  | 'top'
  | 'middle'
  | 'bottom'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'center'
  | 'above_title'
  | 'below_title'
  | 'spine'
  | 'front'  // For full_wrap covers - element appears on front
  | 'back';  // For full_wrap covers - element appears on back

export type ElementPriority = 'required' | 'recommended' | 'optional';

export interface CoverElement {
  type: ElementType;
  content: string;
  placement: ElementPlacement;
  priority: ElementPriority;
  size?: 'small' | 'medium' | 'large' | 'dominant';
  style?: {
    font?: string;
    color?: string;
    background?: string;
    opacity?: number;
  };
}

// Element definitions with metadata
export const ELEMENT_DEFINITIONS: Record<ElementType, {
  label: string;
  description: string;
  icon: string;
  defaultPlacement: Record<CoverType, ElementPlacement>;
  defaultPriority: Record<CoverType, ElementPriority>;
  maxLength?: number;
  publishingNotes?: string;
}> = {
  title: {
    label: 'Book Title',
    description: 'Main book title - largest text element',
    icon: '📖',
    defaultPlacement: { front: 'top', back: 'top', spine: 'center', full_wrap: 'front' },
    defaultPriority: { front: 'required', back: 'optional', spine: 'required', full_wrap: 'required' },
    maxLength: 100,
    publishingNotes: 'Must be readable at thumbnail size (80px wide)'
  },
  author: {
    label: 'Author Name',
    description: 'Author name - second hierarchy after title',
    icon: '✍️',
    defaultPlacement: { front: 'bottom', back: 'bottom', spine: 'bottom', full_wrap: 'front' },
    defaultPriority: { front: 'required', back: 'required', spine: 'required', full_wrap: 'required' },
    maxLength: 50,
    publishingNotes: 'Sometimes equal prominence to title for brand authors'
  },
  subtitle: {
    label: 'Subtitle/Tagline',
    description: 'Book subtitle or descriptive tagline',
    icon: '💭',
    defaultPlacement: { front: 'below_title', back: 'top', spine: 'center', full_wrap: 'front' },
    defaultPriority: { front: 'optional', back: 'optional', spine: 'optional', full_wrap: 'optional' },
    maxLength: 150
  },
  series: {
    label: 'Series Information',
    description: 'Series name and book number',
    icon: '📚',
    defaultPlacement: { front: 'below_title', back: 'top', spine: 'top', full_wrap: 'front' },
    defaultPriority: { front: 'recommended', back: 'recommended', spine: 'recommended', full_wrap: 'recommended' },
    maxLength: 80,
    publishingNotes: 'Important for multi-book works and reader discovery'
  },
  tagline: {
    label: 'Hook/Tagline',
    description: 'Catchy marketing hook or memorable phrase',
    icon: '🎯',
    defaultPlacement: { front: 'above_title', back: 'top', spine: 'center', full_wrap: 'front' },
    defaultPriority: { front: 'optional', back: 'recommended', spine: 'optional', full_wrap: 'optional' },
    maxLength: 100
  },
  endorsement: {
    label: 'Endorsements/Reviews',
    description: 'Review quotes and endorsements',
    icon: '💬',
    defaultPlacement: { front: 'top', back: 'top', spine: 'center', full_wrap: 'front' },
    defaultPriority: { front: 'optional', back: 'recommended', spine: 'optional', full_wrap: 'optional' },
    maxLength: 200,
    publishingNotes: 'Use 1-3 high-impact quotes for credibility'
  },
  award: {
    label: 'Awards/Badges',
    description: 'Awards, bestseller status, accolades',
    icon: '🏆',
    defaultPlacement: { front: 'top-right', back: 'top-right', spine: 'top', full_wrap: 'front' },
    defaultPriority: { front: 'recommended', back: 'recommended', spine: 'optional', full_wrap: 'recommended' },
    maxLength: 50,
    publishingNotes: 'Builds credibility and social proof'
  },
  publisher_logo: {
    label: 'Publisher Logo',
    description: 'Publisher or imprint logo/branding',
    icon: '🏢',
    defaultPlacement: { front: 'bottom-left', back: 'bottom-left', spine: 'bottom', full_wrap: 'front' },
    defaultPriority: { front: 'optional', back: 'recommended', spine: 'optional', full_wrap: 'optional' },
    publishingNotes: 'Important for brand consistency and professional appearance'
  },
  blurb: {
    label: 'Book Blurb/Synopsis',
    description: 'Main book description (150-250 words)',
    icon: '📝',
    defaultPlacement: { front: 'middle', back: 'middle', spine: 'center', full_wrap: 'back' },
    defaultPriority: { front: 'optional', back: 'required', spine: 'optional', full_wrap: 'required' },
    maxLength: 500,
    publishingNotes: 'Essential for back cover - primary sales tool'
  },
  bio: {
    label: 'Author Bio',
    description: 'Short author biography',
    icon: '👤',
    defaultPlacement: { front: 'bottom', back: 'bottom', spine: 'bottom', full_wrap: 'back' },
    defaultPriority: { front: 'optional', back: 'recommended', spine: 'optional', full_wrap: 'recommended' },
    maxLength: 200,
    publishingNotes: 'Builds author credibility and connection with readers'
  },
  author_photo: {
    label: 'Author Photo',
    description: 'Professional author headshot',
    icon: '📷',
    defaultPlacement: { front: 'bottom-right', back: 'bottom-right', spine: 'center', full_wrap: 'back' },
    defaultPriority: { front: 'optional', back: 'optional', spine: 'optional', full_wrap: 'optional' },
    publishingNotes: 'Helps with author branding and reader connection'
  },
  barcode: {
    label: 'ISBN Barcode',
    description: 'ISBN barcode for retail scanning',
    icon: '🔢',
    defaultPlacement: { front: 'bottom-right', back: 'bottom-right', spine: 'center', full_wrap: 'back' },
    defaultPriority: { front: 'optional', back: 'required', spine: 'optional', full_wrap: 'required' },
    publishingNotes: 'Mandatory for print books - requires 2.0" × 1.2" clear area'
  },
  isbn: {
    label: 'ISBN Number',
    description: 'ISBN number in text format',
    icon: '🔢',
    defaultPlacement: { front: 'bottom', back: 'bottom-left', spine: 'bottom', full_wrap: 'back' },
    defaultPriority: { front: 'optional', back: 'recommended', spine: 'optional', full_wrap: 'recommended' },
    maxLength: 20
  },
  price: {
    label: 'Book Price',
    description: 'Retail price information',
    icon: '💰',
    defaultPlacement: { front: 'top-right', back: 'bottom-right', spine: 'bottom', full_wrap: 'back' },
    defaultPriority: { front: 'optional', back: 'recommended', spine: 'optional', full_wrap: 'recommended' },
    maxLength: 20,
    publishingNotes: 'Often required for print distribution'
  },
  qr_code: {
    label: 'QR Code',
    description: 'QR code for additional content or website',
    icon: '🔗',
    defaultPlacement: { front: 'bottom-right', back: 'bottom-left', spine: 'center', full_wrap: 'back' },
    defaultPriority: { front: 'optional', back: 'optional', spine: 'optional', full_wrap: 'optional' },
    publishingNotes: 'Modern marketing tool for digital engagement'
  },
  social_media: {
    label: 'Social Media',
    description: 'Social media handles and websites',
    icon: '📱',
    defaultPlacement: { front: 'bottom', back: 'bottom', spine: 'bottom', full_wrap: 'back' },
    defaultPriority: { front: 'optional', back: 'optional', spine: 'optional', full_wrap: 'optional' },
    maxLength: 100,
    publishingNotes: 'Helps build author platform and reader engagement'
  },
  publisher_info: {
    label: 'Publisher Information',
    description: 'Publisher name, website, and contact info',
    icon: '🏢',
    defaultPlacement: { front: 'bottom-left', back: 'bottom-left', spine: 'bottom', full_wrap: 'back' },
    defaultPriority: { front: 'optional', back: 'optional', spine: 'optional', full_wrap: 'optional' },
    maxLength: 100
  }
};

// Get elements appropriate for a specific cover type
export function getElementsForCoverType(coverType: CoverType): ElementType[] {
  const allElements = Object.keys(ELEMENT_DEFINITIONS) as ElementType[];

  switch (coverType) {
    case 'front':
      return allElements.filter(type =>
        ELEMENT_DEFINITIONS[type].defaultPriority.front !== 'optional' ||
        ['title', 'author', 'subtitle', 'series', 'tagline', 'endorsement', 'award', 'publisher_logo'].includes(type)
      );

    case 'back':
      return allElements.filter(type =>
        ELEMENT_DEFINITIONS[type].defaultPriority.back !== 'optional' ||
        ['blurb', 'bio', 'endorsement', 'author_photo', 'barcode', 'isbn', 'price', 'publisher_info', 'social_media', 'qr_code'].includes(type)
      );

    case 'spine':
      return ['title', 'author', 'publisher_logo', 'series'];

    case 'full_wrap':
      return allElements;

    default:
      return allElements;
  }
}

// Get required elements for a cover type
export function getRequiredElements(coverType: CoverType): ElementType[] {
  return (Object.keys(ELEMENT_DEFINITIONS) as ElementType[]).filter(type =>
    ELEMENT_DEFINITIONS[type].defaultPriority[coverType] === 'required'
  );
}

// Get recommended elements for a cover type
export function getRecommendedElements(coverType: CoverType): ElementType[] {
  return (Object.keys(ELEMENT_DEFINITIONS) as ElementType[]).filter(type =>
    ELEMENT_DEFINITIONS[type].defaultPriority[coverType] === 'recommended'
  );
}

// Create a cover element with defaults
export function createElement(
  type: ElementType,
  content: string,
  coverType: CoverType,
  overrides?: Partial<CoverElement>
): CoverElement {
  const definition = ELEMENT_DEFINITIONS[type];

  return {
    type,
    content,
    placement: definition.defaultPlacement[coverType],
    priority: definition.defaultPriority[coverType],
    ...overrides
  };
}

// Validate element content length
export function validateElement(element: CoverElement): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  const definition = ELEMENT_DEFINITIONS[element.type];

  if (!element.content || element.content.trim().length === 0) {
    if (element.priority === 'required') {
      errors.push(`${definition.label} is required and cannot be empty`);
    }
  } else if (definition.maxLength && element.content.length > definition.maxLength) {
    errors.push(`${definition.label} exceeds maximum length of ${definition.maxLength} characters`);
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

// Generate element groups for UI organization
export interface ElementGroup {
  title: string;
  description: string;
  elements: ElementType[];
  coverTypes: CoverType[];
}

export const ELEMENT_GROUPS: ElementGroup[] = [
  {
    title: 'Core Information',
    description: 'Essential book and author information',
    elements: ['title', 'author', 'subtitle', 'series'],
    coverTypes: ['front', 'back', 'spine', 'full_wrap']
  },
  {
    title: 'Marketing Elements',
    description: 'Promotional and credibility-building content',
    elements: ['tagline', 'endorsement', 'award', 'publisher_logo'],
    coverTypes: ['front', 'back', 'full_wrap']
  },
  {
    title: 'Back Cover Content',
    description: 'Content specific to back cover layout',
    elements: ['blurb', 'bio', 'author_photo'],
    coverTypes: ['back', 'full_wrap']
  },
  {
    title: 'Publishing Information',
    description: 'Technical publishing and retail information',
    elements: ['barcode', 'isbn', 'price', 'publisher_info'],
    coverTypes: ['back', 'spine', 'full_wrap']
  },
  {
    title: 'Digital & Social',
    description: 'Modern marketing and engagement tools',
    elements: ['qr_code', 'social_media'],
    coverTypes: ['back', 'full_wrap']
  }
];

// Generate a complete element set for a cover type with sensible defaults
export function generateDefaultElements(
  coverType: CoverType,
  bookMetadata: {
    title?: string;
    author?: string;
    subtitle?: string;
    series?: string;
    blurb?: string;
    isbn?: string;
  },
  authorDetails?: {
    authorName?: string;
    publisherLogoBase64?: string | null;
    frontCoverEndorsements?: string;
    awardsAndBadges?: string[];
    backCoverHookLine?: string;
    backCoverEndorsements?: string;
    seriesDisplayInfo?: string;
    qrCodeUrl?: string;
    socialMediaHandles?: string;
    publisher?: string;
    authorBio?: string;
    authorProfileImageBase64?: string | null;
  }
): CoverElement[] {
  const elements: CoverElement[] = [];
  const requiredElements = getRequiredElements(coverType);
  const recommendedElements = getRecommendedElements(coverType);

  // Add required elements
  requiredElements.forEach(type => {
    let content = '';

    switch (type) {
      case 'title':
        content = bookMetadata.title || '';
        break;
      case 'author':
        content = bookMetadata.author || authorDetails?.authorName || '';
        break;
      case 'blurb':
        content = bookMetadata.blurb || '';
        break;
      case 'bio':
        content = authorDetails?.authorBio || '';
        break;
      case 'barcode':
        content = bookMetadata.isbn || '';
        break;
    }

    if (content) {
      elements.push(createElement(type, content, coverType));
    }
  });

  // Add recommended elements with available data
  recommendedElements.forEach(type => {
    let content = '';

    switch (type) {
      case 'subtitle':
        content = bookMetadata.subtitle || '';
        break;
      case 'series':
        content = authorDetails?.seriesDisplayInfo || bookMetadata.series || '';
        break;
      case 'endorsement':
        content = coverType === 'front'
          ? (authorDetails?.frontCoverEndorsements || '')
          : (authorDetails?.backCoverEndorsements || '');
        break;
      case 'award':
        content = authorDetails?.awardsAndBadges?.[0] || '';
        break;
      case 'tagline':
        content = authorDetails?.backCoverHookLine || '';
        break;
      case 'isbn':
        content = bookMetadata.isbn || '';
        break;
      case 'publisher_info':
        content = authorDetails?.publisher || '';
        break;
      case 'social_media':
        content = authorDetails?.socialMediaHandles || '';
        break;
      case 'qr_code':
        content = authorDetails?.qrCodeUrl || '';
        break;
    }

    if (content) {
      elements.push(createElement(type, content, coverType));
    }
  });

  return elements;
}